/**
 * Date calculation utilities for trade booking
 */
import i18n from '../locales/i18n';

/**
 * Calculate value date by adding offset days to trade date
 * @param tradeDate - Base trade date
 * @param offset - Number of days to add (0 for T+0, 1 for T+1, 2 for T+2, etc.)
 * @returns Calculated date
 */
export const calculateValueDate = (tradeDate: Date, offset: number): Date => {
  const valueDate = new Date(tradeDate);
  valueDate.setDate(valueDate.getDate() + offset);
  return valueDate;
};

/**
 * Calculate number of calendar days between two dates
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Number of days
 */
export const calculateDaysDifference = (startDate: Date, endDate: Date): number => {
  const timeDiff = endDate.getTime() - startDate.getTime();
  return Math.floor(timeDiff / (1000 * 60 * 60 * 24));
};

/**
 * Add days to a date
 * @param date - Base date
 * @param days - Number of days to add
 * @returns New date
 */
export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Add months to a date (approximation: 30 days per month)
 * @param date - Base date
 * @param months - Number of months to add
 * @returns New date
 */
export const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date);
  // Use actual month addition for more accurate calculation
  result.setMonth(result.getMonth() + months);
  return result;
};

/**
 * Add years to a date (approximation: 365 days per year)
 * @param date - Base date
 * @param years - Number of years to add
 * @returns New date
 */
export const addYears = (date: Date, years: number): Date => {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
};

/**
 * Calculate maturity date from value date and maturity days
 * @param valueDate - Base value date
 * @param maturityDays - Number of days in maturity
 * @returns Calculated maturity date (expiry date)
 */
export const calculateMaturityDate = (valueDate: Date, maturityDays: number): Date => {
  return addDays(valueDate, maturityDays);
};

/**
 * Format maturity from days to "1Y3M2D" format
 * Approximation: 365 days = 1 year, 30 days = 1 month
 * @param days - Total number of days
 * @returns Formatted string (e.g., "1Y3M2D")
 */
export const formatMaturityFromDays = (days: number): string => {
  if (days === 0) return '0D';

  let remaining = days;
  const years = Math.floor(remaining / 365);
  remaining = remaining % 365;

  const months = Math.floor(remaining / 30);
  remaining = remaining % 30;

  const resultDays = remaining;

  const parts: string[] = [];
  if (years > 0) parts.push(`${years}Y`);
  if (months > 0) parts.push(`${months}M`);
  if (resultDays > 0) parts.push(`${resultDays}D`);

  return parts.join('') || '0D';
};

/**
 * Format date to YYYY-MM-DD for input[type="date"]
 * @param date - Date to format
 * @returns Formatted string
 */
export const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse date from input[type="date"] string
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object or null if invalid
 */
export const parseDateFromInput = (dateString: string): Date | null => {
  if (!dateString) return null;
  // Parse as local date to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Parse a trade date string in DD-MM-YYYY or YYYY-MM-DD format to a Date object.
 * Returns null for empty, 'N/A', or unparseable input.
 */
export const parseTradeDate = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr || dateStr === 'N/A') return null;
  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;
  let year: number, month: number, day: number;
  if (parts[0].length === 4) {
    [year, month, day] = parts.map(Number);
  } else {
    [day, month, year] = parts.map(Number);
  }
  if (!year || !month || !day) return null;
  const d = new Date(year, month - 1, day);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Map i18n language code to a date locale for toLocaleDateString().
 */
const DATE_LOCALE_MAP: Record<string, string> = {
  es: 'es-CL',
  en: 'en-GB',
  pt: 'pt-BR',
};

/**
 * Get the date locale based on the current i18n language.
 */
const getDateLocale = (): string => {
  return DATE_LOCALE_MAP[i18n.language] || 'es-CL';
};

/**
 * Format a Firestore trade date (DD-MM-YYYY or YYYY-MM-DD) for display as "ddd DD-MM-YYYY".
 * Detects the format automatically. Uses the app's current locale for the day name.
 * Returns '' for falsy input, 'N/A' for 'N/A'.
 */
export const formatTradeDate = (dateStr: string, locale?: string): string => {
  if (!dateStr) return '';
  if (dateStr === 'N/A') return 'N/A';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  let day: string, month: string, year: string;
  if (parts[0].length === 4) {
    [year, month, day] = parts;
  } else {
    [day, month, year] = parts;
  }
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (isNaN(date.getTime())) return dateStr;
  const resolvedLocale = locale || getDateLocale();
  const dayName = date.toLocaleDateString(resolvedLocale, { weekday: 'short' });
  return `${dayName} ${day}-${month}-${year}`;
};
